import { useEffect, useRef } from "react";
import { useHistory } from "react-router";

/**
 * Client inactivity threshold before a resume interaction triggers
 * a session/status check. Adjust this value (seconds) as needed.
 * Default: 5 minutes = 300 seconds.
 */
export const SESSION_IDLE_THRESHOLD_SECONDS = 30;

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "click"];

/**
 * Calls GET /api/session/status only on the first user interaction
 * after ≥ SESSION_IDLE_THRESHOLD_SECONDS of no activity.
 * Invalid session teardown is handled by checkSessionStatus; this hook redirects.
 */
export default function useSessionStatusWatcher(checkSessionStatus) {
  const history = useHistory();
  const inFlightRef = useRef(false);
  const lastActivityAtRef = useRef(Date.now());

  useEffect(() => {
    if (typeof checkSessionStatus !== "function") return undefined;

    const redirectIfNeeded = () => {
      const path = history.location.pathname;
      if (path === "/auth/login" || path === "/auth/verification") return;
      history.push("/auth/login");
    };

    const runCheck = async () => {
      if (!localStorage.getItem("loginToken") || inFlightRef.current) return;

      inFlightRef.current = true;
      try {
        const result = await checkSessionStatus();
        if (result?.valid === false && !result?.skipped) {
          redirectIfNeeded();
        }
      } catch {
        // Transient errors: keep current session UI
      } finally {
        inFlightRef.current = false;
      }
    };

    const onActivity = () => {
      const now = Date.now();
      const idleMs = now - lastActivityAtRef.current;
      const thresholdMs = SESSION_IDLE_THRESHOLD_SECONDS * 1000;
      const isFirstInteractionAfterIdle = idleMs >= thresholdMs;

      lastActivityAtRef.current = now;

      if (isFirstInteractionAfterIdle) {
        void runCheck();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, onActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, onActivity);
      });
    };
  }, [checkSessionStatus, history]);
}
